/**
 * Shared <countdown-timer> — split digits (.times_day_tens) and combined (.times_day).
 * data-date: 2025/03/05 23:59:59, or with a timezone suffix such as PST.
 * No suffix uses the visitor's local time. PST/PDT/EST and the other listed suffixes are fixed offsets.
 */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.countdownElements = {
        days: this.querySelector('.times_day'),
        hours: this.querySelector('.times_hour'),
        minutes: this.querySelector('.times_minute'),
        seconds: this.querySelector('.times_second'),
        days_1: this.querySelector('.times_day1'),
        days_2: this.querySelector('.times_day2'),
        hours_1: this.querySelector('.times_hour1'),
        hours_2: this.querySelector('.times_hour2'),
        minutes_1: this.querySelector('.times_minute1'),
        minutes_2: this.querySelector('.times_minute2'),
        seconds_1: this.querySelector('.times_second1'),
        seconds_2: this.querySelector('.times_second2'),
        daysTens: this.querySelector('.times_day_tens'),
        daysOnes: this.querySelector('.times_day_ones'),
        hoursTens: this.querySelector('.times_hour_tens'),
        hoursOnes: this.querySelector('.times_hour_ones'),
        minutesTens: this.querySelector('.times_minute_tens'),
        minutesOnes: this.querySelector('.times_minute_ones'),
        secondsTens: this.querySelector('.times_second_tens'),
        secondsOnes: this.querySelector('.times_second_ones'),
        daysBlock: this.querySelector('.components-countdown-time_items--days'),
        daysOnesSymbol: this.querySelector('.times_day_ones_symbol'),
        countdownText: this.querySelector('.components-countdown-text'),
        // 整句倒计时，给读屏用；没有这个节点的倒计时不受影响
        liveText: this.querySelector('.countdown_sr'),
      };

      this.targetDate = this._parseDate(this.getAttribute('data-date'));
      this.updateCountdown();
      this._timer = window.setInterval(() => this.updateCountdown(), 1000);
    }

    disconnectedCallback() {
      if (this._timer) window.clearInterval(this._timer);
    }

    /**
     * @param {string | null} raw
     * @returns {number}
     */
    _parseDate(raw) {
      if (!raw) return NaN;
      const text = String(raw).trim();
      const zoneMatch = text.match(/\s+([A-Za-z]{2,5})$/);
      const zone = zoneMatch ? zoneMatch[1].toUpperCase() : '';
      const stripped = zone ? text.slice(0, zoneMatch.index).trim() : text;
      const normalized = stripped.replace(/\//g, '-');
      const match = normalized.match(
        /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/
      );
      if (match) {
        const y = Number(match[1]);
        const mo = Number(match[2]);
        const d = Number(match[3]);
        const h = Number(match[4] || '0');
        const mi = Number(match[5] || '0');
        const s = Number(match[6] || '0');
        const offset = this._zoneOffsetHours(zone);
        // 没写时区时沿用访问者本地时间。写了 PST 这类后缀就按该时区，避免在北京打开时把钟点当地时间
        if (offset == null) return new Date(y, mo - 1, d, h, mi, s).getTime();
        return Date.UTC(y, mo - 1, d, h, mi, s) - offset * 3600000;
      }
      return new Date(raw).getTime();
    }

    /**
     * 固定偏移（小时）。PST 始终是 UTC-8，不随夏令时切换。
     * @param {string} zone
     * @returns {number | null}
     */
    _zoneOffsetHours(zone) {
      const offsets = {
        PST: -8,
        PDT: -7,
        PT: -8,
        EST: -5,
        EDT: -4,
        ET: -5,
        CST: -6,
        CDT: -5,
        CT: -6,
        MST: -7,
        MDT: -6,
        MT: -7,
        AKST: -9,
        AKDT: -8,
        HST: -10,
        GMT: 0,
        UTC: 0,
      };
      if (!zone || !Object.prototype.hasOwnProperty.call(offsets, zone)) return null;
      return offsets[zone];
    }

    /**
     * @param {number} value
     * @returns {{ tens: string, ones: string, padded: string }}
     */
    _parts(value) {
      const safe = Math.max(0, value);
      const padded = String(safe).padStart(2, '0');
      return {
        tens: String(Math.floor(safe / 10)),
        ones: String(safe % 10),
        padded,
      };
    }

    updateCountdown() {
      if (!Number.isFinite(this.targetDate)) return;

      const timeRemaining = this.targetDate - Date.now();
      if (timeRemaining <= 0) {
        this.resetCountdown();
        return;
      }

      const days = Math.floor(timeRemaining / 86400000);
      const hours = Math.floor((timeRemaining % 86400000) / 3600000);
      const minutes = Math.floor((timeRemaining % 3600000) / 60000);
      const seconds = Math.floor((timeRemaining % 60000) / 1000);

      this._setDigits(days, hours, minutes, seconds);
      this._toggleDays(days > 0);
      this._syncAccessibleTime(days, hours, minutes, seconds);
    }

    /**
     * 视觉上数字和单位是分开的节点，读屏会拆开循环读。
     * 视觉层已 aria-hidden，这里把「前缀 + 天时分秒」写成一句，刷新时整段播报。
     * @param {number} days
     * @param {number} hours
     * @param {number} minutes
     * @param {number} seconds
     */
    _syncAccessibleTime(days, hours, minutes, seconds) {
      const live = this.countdownElements.liveText;
      if (!live) return;

      const prefix = (this.querySelector('.countdown_text')?.textContent || '').replace(/\s+/g, ' ').trim();
      const sentence = `${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`;
      const next = prefix ? `${prefix.replace(/[,:]\s*$/, '')}, ${sentence}` : sentence;
      if (live.textContent !== next) live.textContent = next;
    }

    /**
     * @param {number} days
     * @param {number} hours
     * @param {number} minutes
     * @param {number} seconds
     */
    _setDigits(days, hours, minutes, seconds) {
      const d = this._parts(days);
      const h = this._parts(hours);
      const m = this._parts(minutes);
      const s = this._parts(seconds);

      this.updateElement(this.countdownElements.days, d.padded);
      this.updateElement(this.countdownElements.hours, h.padded);
      this.updateElement(this.countdownElements.minutes, m.padded);
      this.updateElement(this.countdownElements.seconds, s.padded);

      this.updateElement(this.countdownElements.days_1, d.tens);
      this.updateElement(this.countdownElements.days_2, d.ones);
      this.updateElement(this.countdownElements.hours_1, h.tens);
      this.updateElement(this.countdownElements.hours_2, h.ones);
      this.updateElement(this.countdownElements.minutes_1, m.tens);
      this.updateElement(this.countdownElements.minutes_2, m.ones);
      this.updateElement(this.countdownElements.seconds_1, s.tens);
      this.updateElement(this.countdownElements.seconds_2, s.ones);

      this.updateElement(this.countdownElements.daysTens, d.tens);
      this.updateElement(this.countdownElements.daysOnes, d.ones);
      this.updateElement(this.countdownElements.hoursTens, h.tens);
      this.updateElement(this.countdownElements.hoursOnes, h.ones);
      this.updateElement(this.countdownElements.minutesTens, m.tens);
      this.updateElement(this.countdownElements.minutesOnes, m.ones);
      this.updateElement(this.countdownElements.secondsTens, s.tens);
      this.updateElement(this.countdownElements.secondsOnes, s.ones);
    }

    /**
     * @param {boolean} show
     */
    _toggleDays(show) {
      if (this.countdownElements.daysBlock) {
        this.countdownElements.daysBlock.style.display = show ? '' : 'none';
      }
      if (this.countdownElements.daysOnesSymbol) {
        this.countdownElements.daysOnesSymbol.style.display = show ? '' : 'none';
      }
    }

    /**
     * @param {Element | null} element
     * @param {string} value
     */
    updateElement(element, value) {
      if (element) element.textContent = value;
    }

    resetCountdown() {
      this._setDigits(0, 0, 0, 0);
      this._syncAccessibleTime(0, 0, 0, 0);
      this.querySelectorAll('.components-countdown-time_items').forEach((item) => {
        item.style.display = '';
      });
      this._toggleDays(false);
      if (this.countdownElements.countdownText) {
        this.countdownElements.countdownText.hidden = false;
        this.countdownElements.countdownText.textContent = "Oops! It's over!";
      }
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}
